import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CheckBox,
  Heading,
  Image,
  Layer,
  Spinner,
  Text,
} from "grommet";
import { FormClose } from "grommet-icons";
import { CategoryService, ProductService, RoomTypeService } from "@services";
import PhotoService from "../../../services/photo.service";
import { roundPrice } from "@common/utils";
import { SortableList } from "@shared";
import noPhoto from "../../../images/nophoto.jpg";
import { presentToastErrorContent } from "@shared/Toast";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { useFetchHotel } from "../redux/hooks";
import "./EmailUpsellsModal.scss";

const getCategoryId = (product) =>
  product.category?._id || product.category || null;

const getSelectionKey = (selection) => `${selection.kind}:${selection.id}`;

const specialUpsells = [
  { kind: "earlyCheckin", photo: "/images/earlyCheckin.jpg" },
  { kind: "lateCheckout", photo: "/images/lateCheckout.jpg" },
  { kind: "upgrade", photo: "/images/upgrade.jpg" },
];

const getDateStatus = (schedules, timezone) => {
  const localDate = moment()
    .tz(timezone || "UTC")
    .format("YYYY-MM-DD");
  if (schedules.some((schedule) => schedule?.endDate < localDate)) {
    return "expired";
  }
  if (schedules.some((schedule) => schedule?.startDate > localDate)) {
    return "notStarted";
  }
  return null;
};

export default function EmailUpsellsModal({
  selections = [],
  currency,
  onSave,
  onClose,
}) {
  const { t } = useTranslation();
  const { hotel } = useFetchHotel();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [draft, setDraft] = useState(selections);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      CategoryService.getList(),
      ProductService.getProducts(),
      RoomTypeService.getRoomTypes().catch(() => ({ data: [] })),
    ]).then(
      ([categoriesResponse, productsResponse, roomTypesResponse]) => {
        setCategories(
          (categoriesResponse.data || []).filter(
            (category) => category?._id && category.active !== false,
          ),
        );
        setProducts(
          (productsResponse.data || []).filter(
            (product) => product.active !== false,
          ),
        );
        setRoomTypes(roomTypesResponse.data || []);
        setLoading(false);
      },
      (error) => {
        setLoading(false);
        presentToastErrorContent(error);
      },
    );
  }, []);

  const itemsByKey = useMemo(() => {
    const nextUpgradeRoomPhoto = [...roomTypes]
      .sort((first, second) => (first.order || 0) - (second.order || 0))
      .find((roomType) => roomType.upgradePrice > 0 && roomType.photo)?.photo;
    const entries = specialUpsells.map(({ kind, photo }) => ({
      kind,
      group: t("emailUpsells.specialUpsells"),
      name: t(`emailUpsells.${kind}`),
      photo: kind === "upgrade" ? nextUpgradeRoomPhoto : undefined,
      staticPhoto: photo,
      priceLabel: t("emailUpsells.pricedPerReservation"),
    }));

    products
      .filter((product) => !getCategoryId(product))
      .forEach((product) =>
        entries.push({
          id: String(product._id),
          kind: "product",
          group: t("emailUpsells.standaloneProducts"),
          name: product.name,
          photo: product.photo,
          price: Number(product.price || 0),
          externalUrl: product.externalUrl,
          dateStatus: getDateStatus(
            [product.consumptionSchedule],
            hotel?.timezone,
          ),
        }),
      );

    categories.forEach((category) => {
      const categoryProducts = products.filter(
        (product) => String(getCategoryId(product)) === String(category._id),
      );
      const prices = categoryProducts.map((product) =>
        Number(product.price || 0),
      );
      entries.push({
        id: String(category._id),
        kind: "category",
        group: category.isMenu
          ? t("emailUpsells.menuCategories")
          : t("emailUpsells.regularCategories"),
        name: category.name,
        photo: category.photo,
        price: prices.length ? Math.min(...prices) : null,
        pricePrefix: prices.length ? t("emailUpsells.from") : "",
        dateStatus: getDateStatus(
          [category.consumptionSchedule],
          hotel?.timezone,
        ),
      });

      if (!category.isMenu) {
        categoryProducts.forEach((product) =>
          entries.push({
            id: String(product._id),
            kind: "product",
            group: category.name,
            name: product.name,
            photo: product.photo,
            price: Number(product.price || 0),
            dateStatus: getDateStatus(
              [category.consumptionSchedule, product.consumptionSchedule],
              hotel?.timezone,
            ),
          }),
        );
      }
    });

    return new Map(entries.map((item) => [getSelectionKey(item), item]));
  }, [categories, hotel?.timezone, products, roomTypes, t]);

  const availableGroups = useMemo(() => {
    const groups = new Map();
    for (const item of itemsByKey.values()) {
      if (!groups.has(item.group)) groups.set(item.group, []);
      groups.get(item.group).push(item);
    }
    return [...groups.entries()];
  }, [itemsByKey]);

  const selectedItems = draft
    .map((selection) => itemsByKey.get(getSelectionKey(selection)))
    .filter(Boolean)
    .map((item) => ({
      ...item,
      selectionId: item.id,
      sortId: getSelectionKey(item),
    }));

  const isSelected = (item) =>
    draft.some(
      (selection) => getSelectionKey(selection) === getSelectionKey(item),
    );

  const toggleItem = (item) => {
    const key = getSelectionKey(item);
    setDraft((current) =>
      current.some((selection) => getSelectionKey(selection) === key)
        ? current.filter((selection) => getSelectionKey(selection) !== key)
        : [...current, { kind: item.kind, id: item.id }],
    );
  };

  const formatPrice = (item) => {
    if (item.price === null || item.price === undefined) return "";
    return `${item.pricePrefix || ""} ${(currency || "USD").toUpperCase()} ${roundPrice(item.price, true)}`.trim();
  };

  const CatalogItem = ({ item, selected, sortable }) => (
    <div
      className={`email-upsells-modal__item${
        sortable ? " email-upsells-modal__item--sortable" : ""
      }${item.dateStatus ? " email-upsells-modal__item--date-inactive" : ""}`}
    >
      {sortable ? (
        <SortableList.DragHandle />
      ) : (
        <CheckBox checked={selected} onChange={() => toggleItem(item)} />
      )}
      <Box className="email-upsells-modal__image" flex={false}>
        <Image
          src={PhotoService.getURL(item.photo) || item.staticPhoto || noPhoto}
          alt=""
          fit="cover"
          style={{ borderRadius: "4px" }}
        />
      </Box>
      <Box className="email-upsells-modal__name" gap="xxsmall">
        <Text weight="bold">{item.name}</Text>
        <Text size="small" color="dark-4">
          {item.kind === "category"
            ? t("emailUpsells.category")
            : t("emailUpsells.product")}
        </Text>
        {item.dateStatus && (
          <Text
            className="email-upsells-modal__date-status"
            size="small"
            weight="bold"
          >
            {t(`emailUpsells.${item.dateStatus}`)}
          </Text>
        )}
      </Box>
      <Text
        className="email-upsells-modal__price"
        size="small"
        weight={item.externalUrl || item.priceLabel ? "normal" : "bold"}
        color={item.externalUrl || item.priceLabel ? "dark-4" : undefined}
      >
        {item.externalUrl
          ? t("emailUpsells.externalLink")
          : item.priceLabel || formatPrice(item)}
      </Text>
      {sortable && (
        <Button
          plain
          icon={<FormClose size="small" />}
          a11yTitle={t("emailUpsells.remove", { name: item.name })}
          onClick={() => toggleItem(item)}
        />
      )}
    </div>
  );

  return (
    <Layer onEsc={onClose} onClickOutside={onClose} responsive>
      <Box className="email-upsells-modal">
        <Box
          className="email-upsells-modal__header"
          direction="row"
          align="center"
          justify="between"
          pad="medium"
          border={{ side: "bottom", color: "light-4" }}
          flex={false}
        >
          <Box gap="xxsmall">
            <Heading level={3} margin="none">
              {t("emailUpsells.title")}
            </Heading>
            <Text size="small" color="dark-4">
              {t("emailUpsells.description")}
            </Text>
            <Text size="small" color="dark-3">
              {t("emailUpsells.availabilityNote")}
            </Text>
          </Box>
          <Button icon={<FormClose />} onClick={onClose} />
        </Box>

        <Box className="email-upsells-modal__content" pad="medium" gap="large">
          {loading ? (
            <Box align="center" pad="large">
              <Spinner />
            </Box>
          ) : (
            <>
              <Box
                className="email-upsells-modal__selected"
                gap="small"
                flex={false}
              >
                <Heading level={4} margin="none">
                  {t("emailUpsells.selected", { count: selectedItems.length })}
                </Heading>
                {selectedItems.length ? (
                  <SortableList
                    items={selectedItems.map((item) => ({
                      ...item,
                      id: item.sortId,
                    }))}
                    onChange={(orderedItems) =>
                      setDraft(
                        orderedItems.map((item) => ({
                          kind: item.kind,
                          id: item.selectionId,
                        })),
                      )
                    }
                    renderItem={(item) => (
                      <SortableList.Item id={item.sortId}>
                        <CatalogItem
                          item={{ ...item, id: item.selectionId }}
                          selected
                          sortable
                        />
                      </SortableList.Item>
                    )}
                  />
                ) : (
                  <Text size="small" color="dark-4">
                    {t("emailUpsells.noneSelected")}
                  </Text>
                )}
              </Box>

              <div className="email-upsells-modal__groups">
                {availableGroups.map(([group, items]) => (
                  <Box
                    className="email-upsells-modal__group"
                    key={group}
                    gap="xsmall"
                    flex={false}
                  >
                    <Heading level={4} margin="none">
                      {group}
                    </Heading>
                    <Box
                      className="email-upsells-modal__items"
                      border={{ color: "light-4" }}
                      round="xsmall"
                    >
                      {items.map((item) => (
                        <CatalogItem
                          key={getSelectionKey(item)}
                          item={item}
                          selected={isSelected(item)}
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </div>
            </>
          )}
        </Box>

        <Box
          className="email-upsells-modal__footer"
          direction="row"
          justify="end"
          gap="small"
          pad="medium"
          border={{ side: "top", color: "light-4" }}
          flex={false}
        >
          <Button label={t("common.cancel")} onClick={onClose} />
          <Button
            primary
            label={t("common.save")}
            onClick={() => onSave(draft)}
            disabled={loading}
          />
        </Box>
      </Box>
    </Layer>
  );
}
